import React, { useState, useEffect } from 'react';
import { formatFloorName } from '../../../../../utils/formatters';
import styles from '../../WizardSteps.module.css';
import floorsStyles from './FloorsStep.module.css';

const DEFAULT_STAIRS = null;

const FloorsStep = ({ data, onChange }) => {
  // numFloors = total floors in home (1 = ground only, 2 = ground + first, etc.)
  const [numFloors, setNumFloors] = useState(data?.numFloors || 1);
  const [hasBasement, setHasBasement] = useState(data?.hasBasement || false);
  const [floors, setFloors] = useState(data?.floors || []);

  // Generate floor data when numFloors or hasBasement changes
  // With basement: floors go from -1 to (numFloors - 2), e.g. 2 floors with basement = -1, 0
  // Without basement: floors go from 0 to (numFloors - 1), e.g. 2 floors = 0, 1
  useEffect(() => {
    setFloors(prevFloors => {
      const newFloors = [];
      const startFloor = hasBasement ? -1 : 0;
      const endFloor = hasBasement ? numFloors - 2 : numFloors - 1;

      for (let floorNum = startFloor; floorNum <= endFloor; floorNum++) {
        const existingFloor = prevFloors.find(f => f.floorNum === floorNum);
        const isTopFloor = floorNum === endFloor;
        const isBasement = floorNum < 0;

        newFloors.push({
          id: `floor-${floorNum}`,
          floorNum,
          name: existingFloor?.name || formatFloorName(floorNum),
          isBasement,
          ceilingHeight: existingFloor?.ceilingHeight ?? null,
          stairs: isTopFloor ? null : (existingFloor?.stairs ?? DEFAULT_STAIRS),
          isTopFloor,
        });
      }

      return newFloors;
    });
  }, [numFloors, hasBasement]);

  // Update parent when data changes
  useEffect(() => {
    onChange({ numFloors, hasBasement, floors });
  }, [numFloors, hasBasement, floors, onChange]);

  const handleFloorChange = (floorId, field, value) => {
    setFloors(prev =>
      prev.map(floor => (floor.id === floorId ? { ...floor, [field]: value } : floor)),
    );
  };

  const incrementFloors = () => {
    if (numFloors < 10) setNumFloors(prev => prev + 1);
  };

  const decrementFloors = () => {
    if (numFloors > 1) setNumFloors(prev => prev - 1);
  };

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>קומות הבית</h1>
        <p className={styles.subtitle}>כמה קומות יש בבית?</p>
      </div>

      {/* Main Controls */}
      <div className={styles.form}>
        {/* Number of Floors Stepper */}
        <div className={styles.field}>
          <label className={styles.label}>מספר קומות</label>
          <div className={floorsStyles.stepper}>
            <button
              type="button"
              className={floorsStyles.stepperButton}
              onClick={decrementFloors}
              disabled={numFloors <= 1}
            >
              −
            </button>
            <span className={floorsStyles.stepperValue}>{numFloors}</span>
            <button
              type="button"
              className={floorsStyles.stepperButton}
              onClick={incrementFloors}
              disabled={numFloors >= 10}
            >
              +
            </button>
          </div>
        </div>

        {/* Basement Toggle - only show if more than 1 floor */}
        {numFloors > 1 && (
          <div className={styles.field}>
            <label className={styles.label}>יש מרתף?</label>
            <div className={floorsStyles.toggleContainer}>
              <button
                type="button"
                className={`${floorsStyles.toggleButton} ${!hasBasement ? floorsStyles.active : ''}`}
                onClick={() => setHasBasement(false)}
              >
                לא
              </button>
              <button
                type="button"
                className={`${floorsStyles.toggleButton} ${hasBasement ? floorsStyles.active : ''}`}
                onClick={() => setHasBasement(true)}
              >
                כן
              </button>
            </div>
          </div>
        )}

        {/* Floor Details */}
        <div className={floorsStyles.floorsList}>
          <label className={styles.label}>פרטי קומות</label>
          {floors.map(floor => (
            <div key={floor.id} className={floorsStyles.floorCard}>
              <div className={floorsStyles.floorHeader}>
                <div className={floorsStyles.floorField}>
                  <label className={floorsStyles.floorFieldLabel}>שם הקומה</label>
                  <input
                    type="text"
                    className={floorsStyles.floorNameInput}
                    value={floor.name}
                    onChange={e => handleFloorChange(floor.id, 'name', e.target.value)}
                    placeholder="שם הקומה"
                  />
                </div>
              </div>
              <div className={floorsStyles.floorDetails}>
                <div className={floorsStyles.floorField}>
                  <label className={floorsStyles.floorFieldLabel}>
                    גובה תקרה (מ׳) <span className={styles.optional}>(אופציונלי)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="2"
                    max="6"
                    className={floorsStyles.floorFieldInput}
                    value={floor.ceilingHeight || ''}
                    onChange={e =>
                      handleFloorChange(
                        floor.id,
                        'ceilingHeight',
                        e.target.value ? parseFloat(e.target.value) : null,
                      )
                    }
                    placeholder="2.7"
                  />
                </div>
                {!floor.isTopFloor && (
                  <div className={floorsStyles.floorField}>
                    <label className={floorsStyles.floorFieldLabel}>מספר מדרגות</label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      className={floorsStyles.floorFieldInput}
                      value={floor.stairs || ''}
                      onChange={e =>
                        handleFloorChange(
                          floor.id,
                          'stairs',
                          e.target.value === '' ? null : parseInt(e.target.value),
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FloorsStep;
